import { DropdownItemStyle } from '@23degrees/shared-code/models';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { SearchPipe } from '@23degrees/shared/pipes/search.pipe';
import { ArraySortPipe } from '@23degrees/shared/pipes/sort.pipe';
import { HighlightSearchPipe } from '@23degrees/shared/pipes/text-highlight.pipe';
import { debounceNext } from '@23degrees/utilities/debounce.utils';
import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { StyleService } from '@23degrees/shared/services/style/style.service';
import { EDITOR_MESSAGES, EVENT_MESSAGES, MESSAGE_RECIPIENTS, REQUEST_MESSAGES } from '@config';
import { v4 as uuid } from 'uuid';
import { ModalOverlayService } from '../../molecules/modal/modal-overlay.service';
import { ModalOverlayComponent } from '../modal/modal-overlay.component';
import { showButtonAnimaton } from './list-selector.animations';

const COMPONENT_NAME_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ListSelectorComponent),
  multi: true,
};

// const ITEMS_STYLE = {
//   padding: '7px 12px',
//   backgroundColor: '#fff',
// };

@Component({
  selector: 'list-selector',
  templateUrl: './list-selector.component.html',
  styleUrls: ['./list-selector.component.scss'],
  providers: [COMPONENT_NAME_VALUE_ACCESSOR],
  animations: [showButtonAnimaton],
  imports: [
    FormsModule,
    NgTemplateOutlet,
    NgClass,
    NgStyle,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    ModalOverlayComponent,
    SearchPipe,
    ArraySortPipe,
    HighlightSearchPipe,
  ],
})
export class ListSelectorComponent extends BaseClass implements OnInit, ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);
  element = inject(ElementRef);
  private messageBus = inject(MessageBus);
  private modalOverlayService = inject(ModalOverlayService);
  private styleService = inject(StyleService);

  onChange: (value) => {};
  onTouched: () => {};

  @HostBinding('class.toTop') toTop: boolean = false;

  @HostBinding('class.toLeft') toLeft: boolean = false;

  @HostBinding('class.alwaysOpen') @Input() alwaysOpen: boolean = true;

  @HostBinding('class.minLineHeight') @Input() minLineHeight: boolean = true;

  @HostBinding('style.maxHeight.px') maxHeight: number;

  @HostBinding('style.maxWidth.px') maxWidth: number;

  @ViewChild('searchContainer', { static: false }) searchContainer: ElementRef;

  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;

  @ViewChildren('results') set sResults(results: QueryList<ElementRef<HTMLElement>>) {
    if (results) {
      this.results = results;
    }
  }
  results: QueryList<ElementRef<HTMLElement>>;

  @Input() items: any = [];

  @Input() multiSelect: boolean = false;

  @Input() valueField: string = 'value';

  @Input() labelField: string = 'label';

  @Input() subLabelField: string | undefined;

  @Input() placeholder: string = 'Suche...';

  @Input() noResultsMessage: string = 'No items found';

  @Input() selectAllText: string = 'Alle auswählen';

  @Input() isLoading = false;

  @Input() resetIcon: string | undefined;

  @Input() query: string = '';

  @Input() trackByFn: any;

  @Input() showSearch: boolean = false;

  @Input() showCheckbox: boolean = false;

  @Input() itemStyle: DropdownItemStyle;

  @Input() itemFont: any;

  @Input() itemInactiveOpacity: number = 0.5;

  @Input() itemMouseOver: any = { backgroundColor: 'rgba(0, 0, 0, 0.05)', color: 'currentColor' };

  @Input() itemActive: any = { backgroundColor: 'transparent', color: 'currentColor' };

  @Input() minLineHeightOnItems: boolean = false;

  @Input() selectAllButtonStyle: any;

  @Input() selectAllButtonBackground: any;

  @Input() backgroundStyle: any;

  @Input() borderRadius: any;

  @Input() boxShadow: any;

  @Input() border?: any;

  @Input() showBoxshadowOnResults: boolean = false;

  @Input() showBackgroundOnResults: boolean = false;

  @Input() showBorderRadiusOnResults: boolean = false;

  @Input() showBorderOnResult: boolean = false;

  @Input() resetButtonStyle;

  @Input() size: number;

  @Input() autoSort: boolean = true;

  @Input() sortDesc: boolean = false;

  @Input() sortByField: string;

  @Input() measureElement: HTMLElement;

  @Input() resultMaxHeightPercentage: number = 60;

  @Input() useVirtualScroll: boolean = false;

  @Input() virtualScrollHeight: string = '30vh';

  @Input() flexed: boolean = false;

  @Input() itemSize: number = 28;

  @Output() newPositions: EventEmitter<{ originPosition: string[]; overlayPosition: string[] }> = new EventEmitter<{
    originPosition: string[];
    overlayPosition: string[];
  }>();

  modalId: string = `SearchInput-${uuid()}`;

  searchFocused: boolean = false;

  selected: any;

  navigateSelect: number = -1;

  autoCloseDelay: number = 1;

  @Input() resultMaxHeight: number;

  @Input() resultMaxWidth: number;

  @Input() resultMinWidth: number;

  searchResultItems: any;

  @Input() originPosition: string[] = ['start', 'bottom'];

  @Input() overlayPosition: string[] = ['start', 'top'];

  @Input() ignoreQuery = false;

  @Output() queryChange = new EventEmitter<string>();

  @Output() onResetQuery = new EventEmitter<void>();

  @Output() itemChange = new EventEmitter<any>();

  @Output() inputFocused = new EventEmitter<void>();

  resetButtonStyleOutput: any = {};

  resetButtonHoverStyleOutput = {};

  resetButtonHover = false;

  private _value: any;

  set value(value: any) {
    this._value = value;
    this.notifyValueChange();
  }

  get value(): any {
    return this._value;
  }

  get resultWidth() {
    return !this.alwaysOpen ? (this.element?.nativeElement ? this.element.nativeElement.clientWidth : null) : null;
  }

  @HostListener('window:keydown', ['$event'])
  catchSearch($event: KeyboardEvent) {
    const ctrlDown = $event.ctrlKey || $event.metaKey;
    if (ctrlDown && $event.key === 'Escape') {
      this.searchInput.nativeElement.focus();
      $event.preventDefault();
    }
  }

  constructor() {
    super();
    this.messageBus.on(EVENT_MESSAGES.Click, MESSAGE_RECIPIENTS.All, this.handleClick, this.componentDestroyed$);
    this.messageBus.on(REQUEST_MESSAGES.ContentResize, 'CATCH_ALL', this.receiveContentResize, this.componentDestroyed$);
    this.messageBus.on(EDITOR_MESSAGES.InvisUpdate, MESSAGE_RECIPIENTS.InvisualFilter, this.receiveContentResize, this.componentDestroyed$);
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges) {
    if ('resetButtonStyle' in changes) {
      this.resetButtonStyleOutput = {
        color: this.resetButtonStyle.color ?? 'inherit',
        ...this.resetButtonStyle.background,
        borderRadius: `${this.resetButtonStyle.borderRadius}px`,
        boxShadow: this.styleService.convertShadow(this.resetButtonStyle.boxShadow),
      };

      this.resetButtonHoverStyleOutput = {
        color: this.resetButtonStyle.hoverStyle.color ?? 'inherit',
        ...this.resetButtonStyle.hoverStyle.background,
        borderRadius: `${this.resetButtonStyle.hoverStyle.borderRadius}px`,
        boxShadow: this.styleService.convertShadow(this.resetButtonStyle.hoverStyle.boxShadow),
      };
    }
  }

  ngAfterViewInit(): void {
    this.calcSizing();
  }

  writeValue(obj: any): void {
    this._value = obj;
    if (this._value) {
      this.selected = JSON.parse(JSON.stringify(this._value));
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {}

  searchChange(fromInputField = false) {
    this.navigateSelect = -1;
    if (fromInputField) {
      this.gotFocus();
    }
    this.cdr.detectChanges();
  }

  getValidValue(item) {
    if (this.valueField) {
      if (typeof item[this.valueField] !== 'undefined' && item[this.valueField] !== null && item[this.valueField] !== undefined) {
        return item[this.valueField];
      }
    }

    return item;
  }

  selectItem(event: MouseEvent, item, resetSelected: boolean = true, forceSingle: boolean = false) {
    this.searchFocused = false;
    if (resetSelected) {
      this.navigateSelect = -1;
    }

    // this.query = item[this.labelField];
    if (this.multiSelect) {
      // if (this.selected.length === this.items.length) {
      if (forceSingle && !(event.ctrlKey || event.altKey || event.metaKey)) {
        this.selected = [this.getValidValue(item)];
      } else {
        const idx = this.selected.indexOf(this.getValidValue(item));
        if (idx > -1) {
          this.selected.splice(idx, 1);
        } else {
          this.selected.push(this.getValidValue(item));
        }
      }
    } else {
      this.selected = this.getValidValue(item);
    }

    if (!this.alwaysOpen) {
      this.modalOverlayService.get(this.modalId).closeModal();
    }

    this.value = this.selected;
    this.itemChange.emit(item);
    this.cdr.detectChanges();
  }

  resetQuery() {
    this.query = '';
    this.navigateSelect = -1;
    // this.autoCloseDelay = 0;
    this.cdr.detectChanges();
    this.onResetQuery.emit();

    setTimeout(() => {
      this.searchInput.nativeElement.blur();
      this.searchInput.nativeElement.focus();
    });
  }

  enterSearch(event) {
    event.preventDefault();
    if (!this.results.length) {
      const modal = this.modalOverlayService.get(this.modalId);
      if (modal && !modal.overlayOpen && !this.alwaysOpen) {
        this.modalOverlayService.get(this.modalId).open();
      }
      return;
    }
    const idx = this.navigateSelect > -1 ? this.navigateSelect : 0;
    const label = this.results.toArray()[idx].nativeElement.querySelector('.label-text').textContent;
    const item = this.items.find((entry) => entry[this.labelField] === label);
    this.selectItem(undefined, item, false);
  }

  up() {
    if (!this.results.length) {
      const modal = this.modalOverlayService.get(this.modalId);
      if (modal && !modal.overlayOpen && !this.alwaysOpen) {
        this.modalOverlayService.get(this.modalId).open();
      }
      return;
    }
    this.navigateSelect = this.navigateSelect > 0 ? this.navigateSelect - 1 : this.results.length - 1;
    this.results.toArray()[this.navigateSelect].nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });

    this.cdr.detectChanges();
  }
  down() {
    if (!this.results.length) {
      const modal = this.modalOverlayService.get(this.modalId);
      if (modal && !modal.overlayOpen && !this.alwaysOpen) {
        this.modalOverlayService.get(this.modalId).open();
      }
      return;
    }
    this.navigateSelect = this.navigateSelect < this.results.length - 1 ? this.navigateSelect + 1 : 0;
    this.results.toArray()[this.navigateSelect].nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });

    this.cdr.detectChanges();
  }

  determinePosition(parent: HTMLElement, child: HTMLElement) {
    const parentBox = parent.getBoundingClientRect();
    const childBox = child.getBoundingClientRect();

    this.toTop = childBox.top - parentBox.top > parentBox.height / 2;
    this.toLeft = childBox.left - parentBox.left > parentBox.width / 2;

    if (this.toTop) {
      this.originPosition = ['start', 'top'];
      this.overlayPosition = ['start', 'bottom'];
    } else {
      this.originPosition = ['start', 'bottom'];
      this.overlayPosition = ['start', 'top'];
    }

    if (this.toLeft) {
      this.originPosition = ['end', this.originPosition[1]];
      this.overlayPosition = ['end', this.overlayPosition[1]];
    }

    this.newPositions.emit({
      overlayPosition: this.overlayPosition,
      originPosition: this.originPosition,
    });

    this.cdr.detectChanges();
  }

  private handleClick = (targetElement) => {
    if (!targetElement || !this.searchFocused) {
      return;
    }
    const clickedInside = this.element.nativeElement.contains(targetElement);
    if (!clickedInside && this.searchFocused) {
      this.searchFocused = false;
      this.cdr.detectChanges();
    }
  };

  gotFocus() {
    this.searchFocused = true;
    const modal = this.modalOverlayService.get(this.modalId);
    if (modal && !modal.overlayOpen && !this.alwaysOpen) {
      this.modalOverlayService.get(this.modalId).open();
    }

    this.inputFocused.emit();

    // if (this.referenceID) {
    // ACTIVATE THIS TO HIDE TOOLTIP ON SEARCH FOCUS
    // this.messageBus.emit('TOOLTIP_CLOSE', [MESSAGE_RECIPIENTS.ToolTip], { emitClose: true}, this.referenceID);
    // }
  }

  closeResults() {
    this.modalOverlayService.get(this.modalId).closeModal();
  }

  receiveContentResize = (msg) => {
    this.calcSizing();
  };

  @debounceNext(50)
  calcSizing() {
    const elem: HTMLElement = this.measureElement || this.element.nativeElement;

    if (!!elem && !!elem.closest) {
      const contentSelector: HTMLElement = elem.closest('content-selector2');
      if (contentSelector) {
        /**
         * TODO: This could calculate the max filling height of the viz, but at the of calculation, the layout is not yet finished with correct positioning
         * So in the meantime: just take percentage of the height
         */
        // const parentBox = contentSelector.getBoundingClientRect();
        // const childBox = elem.getBoundingClientRect();
        // const offset = childBox.top - parentBox.top + elem.clientHeight;
        // console.log(contentSelector.clientHeight, contentSelector.clientHeight - offset);
        const parentBox = contentSelector.getBoundingClientRect();
        const childBox = elem.getBoundingClientRect();

        const toTop = childBox.top - parentBox.top > parentBox.height / 2;
        const toLeft = childBox.left - parentBox.left > parentBox.width / 2;
        let searchHeight = 0;

        if (this.alwaysOpen && this.searchContainer && this.searchContainer.nativeElement) {
          searchHeight = this.searchContainer.nativeElement.clientHeight;
        }

        this.resultMaxHeight = (contentSelector.clientHeight / 100) * this.resultMaxHeightPercentage - searchHeight;
        //const maxHeight = (toTop ? childBox.top - parentBox.top : parentBox.height - (childBox.top - parentBox.top + childBox.height)) - 10;
        const maxWidth = toLeft ? childBox.left - parentBox.left + childBox.width : parentBox.width - (childBox.left - parentBox.left);

        // if (this.alwaysOpen) {
        //   this.maxHeight = maxHeight;
        //   this.maxWidth = maxWidth;
        // } else {
        // this.resultMaxHeight = maxHeight;
        this.resultMaxWidth = maxWidth;
        // }

        this.determinePosition(contentSelector, elem);
      }
    }
  }

  activeItem(item) {
    if (!this.value) return false;
    if (this.multiSelect) {
      if (!Array.isArray(this.value)) return false;
      // if (this.value.length === this.items.length) return false;
      return this.value.some((value) => value === item[this.valueField]);
    }

    if (!this.valueField || !item[this.valueField]) {
      return this.value === item;
    }
    return this.value === item[this.valueField];
  }

  selectAll() {
    this.selected = this.items.map((item) => item[this.valueField]);
    this.value = this.selected;
  }

  trackFunction = (i, item) => {
    if (this.trackByFn) return this.trackByFn(i, item);
    return i || item;
  };

  emitQueryChange(query: string) {
    this.queryChange.emit(query);
  }
}
